/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from "react"
import CustomReactTable from "components/Templates/CustomReactTable"
import TimeCell from "components/Templates/table/TimeCell"
import { useDCRequestsList } from "graphql/stock-take/stock-take.hooks"
import { Icon } from "@blueprintjs/core"
import MultiLineCell from "components/Templates/table/MultiLineCell"
import CustomButton from "components/Atoms/CustomButton"
import ApproveDialog from "./ApproveDialog"
import { Description } from "components/Templates/ModuleHeader"
import BackButton from "components/Templates/BackButton"
import { useDistributionCenterDetails } from "graphql/distribution-center/distribution-center.hooks"
import { useParams } from "react-router-dom"
import HistorySwitch from "./HistorySwitch"
import { useSearchFilter } from "hooks/searchFilterHooks"
import { ErrorToast, SuccessToast } from "utils/AppToaster"
import { useMutation } from "@apollo/client"
import { APPROVE_REQUEST } from "graphql/stock-take/stock-take.mutations.gql"

const DcRequestsList = () => {
  const { data, loading, refetch } = useDCRequestsList()
  const [approveRequest] = useMutation(APPROVE_REQUEST)
  const [refresh, setRefresh] = useState(new Date().getTime())
  const [tableData, setTableData] = useState([])
  const [item, setItem] = useState(null)
  const [isOpen, setOpen] = useState(false)
  const { id } = useParams()
  const [{ view }] = useSearchFilter()
  const [dcDetails, setDetails] = useState(null)
  const [getDetails] = useDistributionCenterDetails(
    ({ distributionCentreLocationDetail: dcl }) => {
      const dc = dcl?.properties

      setDetails({
        id: dcl?.id,
        name: dc?.name,
        dcoName: dc?.primaryPoc?.fullName,
        dcoNumber: dc?.primaryPoc?.number,
      })
    }
  )

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, view])

  useEffect(() => {
    if (parseInt(id)) {
      getDetails({ variables: { id } })
    }
  }, [id, getDetails])

  useEffect(() => {
    if (data && data?.allStockTakeApprovalRequests) {
      let tempList = []

      let batchStocksGroup = data?.allStockTakeApprovalRequests.reduce(
        (acc, curr) => {
          ;(acc[curr["stockEntryItems"]["batchStock"]["codename"]] =
            acc[curr["stockEntryItems"]["batchStock"]["codename"]] || []).push(
            curr
          )
          return acc
        },
        {}
      )

      data?.allStockTakeApprovalRequests?.forEach(request => {
        for (let [key, valueMap] of Object.entries(batchStocksGroup)) {
          if (request["stockEntryItems"]["batchStock"]["codename"] === key) {
            let listSubData = []
            let listId = []
            let freshCount = 0
            let resaleCount = 0
            if (!tempList.some(a => a.codeName === key)) {
              valueMap.forEach(element => {
                if (element["stockEntryItems"]["grade"] === "FRS") {
                  freshCount += element["stockEntryItems"]["quantity"]
                } else {
                  resaleCount += element["stockEntryItems"]["quantity"]
                }
                listId.push(element["id"])

                listSubData.push({
                  grade: element["stockEntryItems"]["grade"],
                  location: element["stockEntryItems"]["subLocation"]["name"],
                  stockTake: element["stockEntryItems"]["quantity"],
                })
              })

              tempList.push({
                status: request["status"],
                sellableId: request["stockEntryItems"]["sellableItem"]["id"],
                sellableItemName:
                  request["stockEntryItems"]["sellableItem"]["name"],
                sellableItemPhotoUrl:
                  request["stockEntryItems"]["sellableItem"]["primaryPhoto"][
                    "url"
                  ],
                batchStockId: request["stockEntryItems"]["batchStock"]["id"],
                codeName: request["stockEntryItems"]["batchStock"]["codename"],
                suppliedByDc:
                  request["stockEntryItems"]["batchStock"]["suppliedBy"]["id"],
                requestedBy: request["requestedBy"]["fullName"],
                requestedByNumber: request["requestedBy"]["number"],
                requestedAt: request["requestedAt"],
                remark: request["remark"],
                freshCount: freshCount,
                resaleCount: resaleCount,
                totalCount: freshCount + resaleCount,
                systemCount: request["stockEntryItems"]["systemCount"],
                listSubData: listSubData,
                listId: listId,
              })
            }
            freshCount = 0
            resaleCount = 0
            listSubData = []
            listId = []
          }
        }
      })

      setTableData(tempList)
    }
  }, [data])

  const COLUMNS = [
    {
      Header: "Requested at",
      accessor: "requestedAt",
      Cell({ value }) {
        return <TimeCell timeStamp={value} />
      },
    },
    {
      Header: "Stock taker",
      accessor: "requestedBy",
      Cell({ value, original }) {
        return (
          <MultiLineCell
            title={original?.requestedBy}
            subtitle={original?.requestedByNumber}
          />
        )
      },
    },
    {
      Header: "Item",
      accessor: "sellableItemName",
      Cell({ value }) {
        return <div>{value}</div>
      },
    },
    {
      Header: "Batch",
      accessor: "codeName",
      Cell({ value }) {
        return <div>{value}</div>
      },
    },
    {
      Header: "Fresh",
      accessor: "freshCount",
      Cell({ value, original }) {
        return <div>{value}</div>
      },
    },
    {
      Header: "Resale",
      accessor: "resaleCount",
      Cell({ value }) {
        return <div>{value}</div>
      },
    },
    {
      Header: "Total",
      accessor: "totalCount",
      Cell({ value }) {
        return <div>{value}</div>
      },
    },
    {
      Header: "Match",
      accessor: "totalCount",
      Cell({ value, original }) {
        if (value === original?.systemCount)
          return (
            <div className="flex items-center">
              <Icon icon="tick-circle" intent="success" />
              <div className="ml-2">No mismatch</div>
            </div>
          )

        if (value < original?.systemCount)
          return (
            <div className="flex items-center">
              <Icon icon="tick-circle" intent="danger" />
              <div className="ml-2">
                {original?.systemCount - value} items less
              </div>
            </div>
          )

        if (value > original?.systemCount)
          return (
            <div className="flex items-center">
              <Icon icon="warning-sign" intent="warning" />
              <div className="ml-2">
                {value - original?.systemCount} items more
              </div>
            </div>
          )
        else return null
      },
    },
    view === "history"
      ? {
          Header: "Status",
          accessor: "status",
          Cell({ value, original }) {
            if (value === "REJ") return <Icon icon="delete" intent="danger" />
            if (value === "APR")
              return <Icon icon="tick-circle" intent="success" />
            else return <Icon icon="tick-circle" intent="warning" />
          },
        }
      : {
          Header: "Actions",
          accessor: "pendingRequests",
          Cell({ value, original }) {
            return (
              <div className="flex items-center justify-around">
                <CustomButton
                  intent="danger"
                  icon="cross"
                  onClick={() => {
                    setItem(original)
                    setOpen(true)
                  }}
                />
                <CustomButton
                  intent="success"
                  icon="tick"
                  onClick={() => {
                    setItem(original)
                    setOpen(true)
                  }}
                />
              </div>
            )
          },
        },
    view === "history"
      ? {
          Header: "Revert",
          accessor: "totalCount",
          Cell({ value, original }) {
            return (
              <CustomButton
                intent="success"
                icon="undo"
                onClick={() => {
                  approveRequest({
                    variables: {
                      obj: {
                        id: original?.listId,
                        status: ["PEN"],
                      },
                    },
                  }).then(({ data, error }) => {
                    if (error) {
                      ErrorToast(error.message)
                      return
                    }
                    setRefresh(new Date().getTime())
                    SuccessToast("Order reverted successfully")
                  })
                }}
              />
            )
          },
        }
      : { hide: true },
  ]
  return (
    <div>
      <section className={"vt-module-header p-3"}>
        <div className="vt-module-description">
          <div className="flex items-center">
            <BackButton className="mr-2" />
            <Description icon="th-list" title={dcDetails?.name} />
          </div>
        </div>
        <div className="vt-module-accessories">
          <HistorySwitch />
        </div>
      </section>
      <CustomReactTable
        loading={loading}
        loadingText="Fetching data"
        data={tableData}
        columns={COLUMNS}
      />
      <ApproveDialog
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        item={item}
        dcDetails={dcDetails}
        refreshData={setRefresh}
      />
    </div>
  )
}

export default DcRequestsList
